import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { ResultData } from '@app/core/_common/model';
import {
  LocationExportData,
  Location,
} from '@app/core/locations/store/locations.model';
import { environment } from '@app/env-service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;
@Injectable({
  providedIn: 'root',
})
export class LocationService {
  url :string = environment.url;
  urlV2 :string = environment.urlV2;
  gettingRecords :boolean;

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';

  locations :any[] = [];

  private readonly _record = new BehaviorSubject<ResultData>(<ResultData>{});
  public record$ = this._record.asObservable();

  private readonly _defaultWarehouseLocations = new BehaviorSubject<any>(<any>[]);
  public defaultWarehouseLocations$ =
    this._defaultWarehouseLocations.asObservable();

  private readonly _gettingDefaultWarehouseLocations = new BehaviorSubject<boolean>(
    <boolean>false,
  );
  public gettingDefaultWarehouseLocations$ =
    this._gettingDefaultWarehouseLocations.asObservable();

  private readonly _warehouseRecord = new BehaviorSubject<ResultData>(
    <ResultData>{},
  );
  public warehouseRecord$ = this._warehouseRecord.asObservable();

  private readonly _countryRecord = new BehaviorSubject<ResultData>(<ResultData>{});
  public countryRecord$ = this._countryRecord.asObservable();

  private readonly _locationsRecord = new BehaviorSubject<any>(<any>[]);
  public locationsRecord$ = this._locationsRecord.asObservable();

  private saveExcelFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  constructor(
    private http :HttpClient,
    private translate :TranslateService,
  ) {}

  get(params :any) {
    return this.http
      .get(`${this.url}/locations`, { params: params })
      .subscribe((resp :ResultData) => {
        this.gettingRecords = false;
        this._record.next(resp);
      });
  }

  getAsync(params :any) {
    return this.http.get(`${this.url}/locations`, { params: params });
  }

  create(data :any) :Observable<any> {
    return this.http.post(`${this.url}/locations`, data).pipe(
      tap((resp :any) => {
        this.locations = [];
      }),
    );
  }

  update(locationId :number, data :any) :Observable<any> {
    return this.http.post(`${this.url}/locations/${locationId}`, data).pipe(
      tap((resp :any) => {
        this.locations = [];
      }),
    );
  }

  updateBulkPicking(locationIds :number[], isPicking :boolean) :Observable<any> {
    return this.http.post(`${this.url}/locations/bulk-update-picking`, {
      locationIds,
      isPicking,
    });
  }

  delete(locationId :number) {
    return this.http.delete(`${this.url}/locations/${locationId}`).pipe(
      tap((resp :any) => {
        this.locations = [];
      }),
    );
  }

  bulkDelete(ids :any) {
    return this.http.post(`${this.url}/locations/bulk-delete`, { ids }).pipe(
      tap((resp :any) => {
        this.locations = [];
      }),
    );
  }

  getAll(params :any) :Observable<any> {
    this.gettingRecords = true;

    return this.http.get(`${this.url}/locations`, { params: params }).pipe(
      tap((resp :any) => {
        this.locations = resp;
        this.gettingRecords = false;
      }),
    );
  }

  deleteAndRevertStocks(locationId :number, data :any = {}) :Observable<any> {
    return this.http.post(`${this.url}/locations/delete/${locationId}`, data);
  }

  getShipstationWarehouses() :Observable<any> {
    return this.http.get(`${this.url}/locations/shipstation_warehouses`);
  }

  importFromFile(fd :FormData) {
    return this.http.post(`${this.url}/locations/import`, fd);
  }

  exportLocationRfqs(payload :any) {
    return this.http.post(`${this.url}/locations/export`, payload);
  }

  exportToCsv(warehouse :string, locations :any[], rfqLocations ?:any) {
    const fileName :string = warehouse + '_locations';
    // Records sheet

    let maxCols :number = 0;
    locations.forEach((loc :any) => {
      loc.skus = rfqLocations.filter((row) => row.location_id == loc.id);
      if (loc.skus.length > maxCols) {
        maxCols = loc.skus.length;
      }
    });
    const jsonData :any = [];
    locations.forEach((loc) => {
      const rowData :any = {
        ID: loc.id,
        LOCATION: loc.location_name,
        IS_PICK_LOCATION: loc.is_pick_location,
      };
      for (let x = 1; x <= maxCols; x++) {
        rowData['SKU' + x.toString()] =
          x <= loc.skus.length ? loc.skus[x - 1].sku : '';
      }
      jsonData.push(rowData);
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  exportLocationsToCsv(warehouse :string, locations :Location[]) {
    const fileName :string = warehouse + '_locations';
    const jsonData :LocationExportData[] = [];
    locations.forEach((loc) => {
      const rowData :LocationExportData = {
        ID: loc.id,
        LOCATION: loc.location_name,
        IS_PICK_LOCATION: loc.is_pick_location,
      };

      if (loc.rfqstock_locations) {
        loc.rfqstock_locations.forEach((stock, idx) => {
          if (stock?.rfq_stock?.rfq?.internal_sku) {
            rowData['SKU' + (idx + 1).toString()] =
              stock?.rfq_stock?.rfq?.internal_sku;
          }
        });
      }
      jsonData.push(rowData);
    });

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');
    const excelBuffer :XLSX.WritingOptions = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    Swal.close();
    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  getWarehouses() {
    return this.http.get(`${this.url}/warehouses`);
  }

  getById(id :number) {
    return this.http.get(`${this.url}/locations/${id}`);
  }

  getPickingLocationsBySkuIds(skuIds :number[]) :Observable<any> {
    return this.http.get(
      `${this.urlV2}/manage/mt-locations/sku-picking-locations`,
      {
        params: { skuIds: skuIds.join(',') },
      },
    );
  }

  getPickingLocationsBySkuId(skuId :number) :Observable<any> {
    return this.http.get(
      `${this.urlV2}/manage/mt-locations/sku-picking-locations`,
      {
        params: { skuId: skuId },
      },
    );
  }

  getPickingLocations() :Observable<any> {
    return this.http.get(`${this.urlV2}/manage/mt-locations/picking-locations`, {});
  }

  getCountryList() :Observable<any> {
    this.gettingRecords = true;

    return this.http.get(`${this.urlV2}/manage/countries`).pipe(
      tap((resp :any) => {
        this.gettingRecords = false;
        this._countryRecord.next(resp);
      }),
    );
  }

  getDefaultWarehouseLocations(params :{ searchQuery :string }) :Subscription {
    this._gettingDefaultWarehouseLocations.next(true);

    return this.http
      .get(`${this.url}/default-warehouse-locations`, { params })
      .subscribe(
        (response :any) => {
          this._gettingDefaultWarehouseLocations.next(false);
          this._defaultWarehouseLocations.next(response);
        },
        (response :any) => {
          this._gettingDefaultWarehouseLocations.next(false);
          throwError(response.error);
        },
      );
  }

  setDefaultWarehouseLocations(locations :any) :void {
    this._defaultWarehouseLocations.next(locations || []);
  }

  getLocations(
    parentId :number,
    withoutStockLocations :boolean = true,
    refreshList :boolean = false,
  ) :Observable<any> {
    this._loading.next(true);
    const locations = this._locationsRecord.getValue();
    const locationsLoaded = locations.some((loc) => loc.parent_id === +parentId);
    const loadLocations = refreshList ? true : !locationsLoaded;

    if (loadLocations) {
      return this.http
        .get(`${this.url}/locations`, {
          params: {
            all: 1,
            parent_id: parentId,
            withoutStockLocations: withoutStockLocations,
          },
        })
        .pipe(
          tap((resp :any) => {
            const locations = this._locationsRecord.getValue();
            this._locationsRecord.next([...locations, ...resp]);
            this._loading.next(false);
          }),
        );
    } else {
      this._loading.next(false);

      return of(locations);
    }
  }

  getAllLocationsWithSelectedColumns(
    selectedColumns :string[] = [],
    parentId :number,
    skuId ?:number | null,
  ) :Observable<any> {
    this._loading.next(true);

    return this.http
      .get(`${this.url}/locations-list`, {
        params: {
          parent_id: parentId,
          ...(skuId && {
            sku_id: skuId,
          }),
          selectedColumns: JSON.stringify(selectedColumns),
        },
      })
      .pipe(
        tap((resp :any) => {
          this._locationsRecord.next(resp);
          this._loading.next(false);
        }),
      );
  }

  getAllLocations(parentId :number) :Observable<any> {
    const locations = this._locationsRecord.getValue();
    const locationsLoaded = locations.some((loc) => loc.parent_id === +parentId);

    if (!locationsLoaded) {
      return this.getAllLocationsWithSelectedColumns(
        ['id', 'parent_id', 'location_name'],
        parentId,
      );
    } else {
      return of(locations);
    }
  }

  exportWarehouseProducts(warehouseId :number, exportType ?:string) :Subscription {
    this.gettingRecords = true;

    return this.http
      .get(`${this.urlV2}/inventory/inventory-quantities/export`, {
        params: {
          warehouseId: warehouseId,
          exportType: exportType,
        },
        responseType: 'blob',
      })
      .subscribe(
        (resp :any) => {
          this.gettingRecords = false;
          FileSaver.saveAs(resp, 'inventory-quantities.xlsx');
          Swal.close();
        },
        (error) => {
          Swal.close();
          Swal.fire('No data found');
        },
      );
  }
}
